import {
  foldSubscriptionFeed,
  formatRoute,
  nextSubscriptionFeedPage,
  subscriptionNotes,
  type SubscriptionFeedPage,
  type SubscriptionListing,
  type SubscriptionNote,
} from '@visious/core';
import { describeAuthProblem, type ProblemCopy, type ProblemSubject } from '../../api/problem';
import type { AuthApiProblem } from '../../api/auth';
import {
  isWatchedVideo,
  matchesGridSearch,
  toListingItems,
  type ListingItem,
  type ProgressLookup,
} from '../../listing/model';
import { text } from '../../strings';
import type { MenuModel } from '../../ui/menuModel';

/**
 * §8's Subscriptions page, as reasoning. R13: no DOM, no React, no `RefObject` — the query
 * plumbing and the visibility observer are in `useSubscriptions.ts`, and `view.tsx` renders props.
 *
 * The page is two lists with different economics, which is the whole shape of the slice:
 *
 * - **The feed** costs one request per page and arrives complete — full listing rows, ~1.3 KB each.
 *   Nothing about it needs hydrating, so it goes straight through 2b's listing layer.
 * - **The subscription list** costs one request per *avatar*, ~50 KB each, and arrives as names and
 *   UCIDs. So it renders text-first and hydrates only what is on screen (`subscriptions/hydrate.ts`).
 *
 * Reading them as one page with one loading state would make the cheap half wait for the expensive
 * one. They are separate queries and separate states here for that reason.
 */

/** The narrowest 16:9 source that covers a grid card at 2× — 2b's measurement, unchanged (`feed/model.ts`). */
const CARD_THUMBNAIL_MIN_WIDTH = 320;

export interface SubscriptionsNotice {
  readonly id: SubscriptionNote['kind'] | 'hidden-seen';
  readonly text: string;
  readonly tone: 'info' | 'caution';
}

/**
 * R7's other side: core decides *which* notes are true (`subscriptionNotes`), the words are ours.
 *
 * The `notified` sentence is the one that had to be written carefully rather than briefly. The obvious
 * copy — "30 new videos" — is false in both directions, and `subscriptions/feed.ts` has the
 * measurements: the flag predates the reader's last visit by up to seventeen days, and the single
 * newest upload in the feed was not carrying it. Saying what the number *is* costs a sentence and is
 * the only version that survives contact with the data.
 */
function noticeFor(note: SubscriptionNote): SubscriptionsNotice | null {
  const words = text().subscriptions.notice;
  switch (note.kind) {
    case 'no-subscriptions':
      return { id: 'no-subscriptions', tone: 'info', text: words.noSubscriptions };
    /*
     * `notified` joined the silent three: the sentence explaining the instance's unseen mark
     * was more caveat than information (the mark itself still reaches assistive tech as each row's
     * spoken marker). The other three report on the response rather than on the feed: rows carrying
     * titles only, rows repeated because the pages shifted under a "Load more", rows that could not
     * be read. Core still counts every one of them — they are how `subscriptions/feed.ts` proves what
     * this endpoint does — but none of them is anything the reader can act on.
     */
    case 'notified':
    case 'titles-only':
    case 'shifted':
    case 'dropped':
      return null;
  }
}

/** One feed row: 2b's listing item, plus the flag. */
export interface SubscriptionFeedItem {
  readonly item: ListingItem;
  readonly notified: boolean;
}

export type SubscriptionsFeedState =
  | { readonly kind: 'loading'; readonly skeletonCards: number }
  | { readonly kind: 'problem'; readonly copy: ProblemCopy }
  | { readonly kind: 'empty'; readonly title: string; readonly detail: string }
  | {
      readonly kind: 'ready';
      readonly items: readonly SubscriptionFeedItem[];
      readonly notices: readonly SubscriptionsNotice[];
      /** The grid search emptied the page — a sentence, or an empty grid reads as a dead feed. */
      readonly nothingMatches: boolean;
      /** `null` when the last page proved it was the last — see `nextSubscriptionFeedPage`. */
      readonly nextPage: number | null;
      readonly listing: SubscriptionListing;
    };

export interface SubscriptionsFeedInput {
  readonly pages: readonly SubscriptionFeedPage[] | null;
  readonly problem: AuthApiProblem | null;
  readonly subscriptionCount: number | null;
  readonly pageSize: number;
  readonly nowEpochSec: number;
  /** 4b's stored playheads (`useWatchProgressLookup`). */
  readonly progress: ProgressLookup;
  /** The seen filter — `settings.hideWatchedInFeed`, threaded so this stays pure. */
  readonly hideSeen: boolean;
  /** The grid search — display-only over the loaded rows, like the seen filter above. */
  readonly search: string;
}

// The "seen" line itself is `isWatchedVideo` in `listing/model.ts` — the stored position where there
// is one, the account's history where there is not. The watched fade and the thumbnails' "Watched"
// mark read the same helper, because what this filter hides must be what those two state.

/**
 * The seen filter as a menu — "a proper grid filter, like on search pages" — built here so
 * `view.tsx` renders props, the search screen's rule. `valueActive` while it hides something, the
 * same accent every set filter wears.
 */
export function watchedFilterMenu(hideSeen: boolean): MenuModel {
  const words = text().subscriptions.page.watched;
  return {
    label: words.label,
    value: hideSeen ? words.hidden : words.shown,
    valueActive: hideSeen,
    sections: [
      {
        title: null,
        items: [
          { id: 'shown', label: words.shown, detail: null, selected: !hideSeen },
          { id: 'hidden', label: words.hidden, detail: null, selected: hideSeen },
        ],
      },
    ],
    note: null,
  };
}

export function subscriptionsFeedState(input: SubscriptionsFeedInput): SubscriptionsFeedState {
  if (input.problem !== null) {
    return { kind: 'problem', copy: describeAuthProblem(input.problem, feedProblemSubject()) };
  }
  if (input.pages === null) return { kind: 'loading', skeletonCards: 12 };

  const listing = foldSubscriptionFeed(input.pages, input.nowEpochSec);
  const notices = subscriptionNotes(listing, input.subscriptionCount).flatMap(
    (note) => noticeFor(note) ?? [],
  );

  if (listing.rows.length === 0) {
    // Distinguished from a failure and from each other, because the two have different next steps and
    // the *response* cannot tell them apart — all of them are `{"notifications":[],"videos":[]}`.
    const words = text().subscriptions.feed;
    if (listing.dropped > 0) {
      return {
        kind: 'problem',
        copy: {
          title: words.allDroppedTitle,
          detail: words.allDropped,
          retryable: true,
        },
      };
    }
    const empty = input.subscriptionCount === 0 ? words.emptyNoChannels : words.emptyNoVideos;
    return { kind: 'empty', title: empty.title, detail: empty.detail };
  }

  const all = listing.rows.map((row, index) => {
    // Through the shared listing layer, one row at a time, with the fold's own scrape passed in:
    // `toListingItems` would otherwise recompute it per call and grade a one-row list degenerate the
    // moment that row happens to be a Short with no duration.
    const [item] = toListingItems([row.video], {
      nowEpochSec: input.nowEpochSec,
      thumbnailMinWidth: CARD_THUMBNAIL_MIN_WIDTH,
      // Genuine here, unlike search and trending: 86 rows carried 86 distinct times of day, so this
      // endpoint's dates are stored rather than back-computed from the relative text.
      absoluteDates: true,
      scrape: listing.scrape,
      progress: input.progress,
    });
    const rendered = item ?? EMPTY_ITEM(row.video.videoId, index);
    return {
      /*
       * `Unseen` overwrites whatever marker 4b put there, and that is the precedence rather than an
       * accident: there is one slot, and this one is *the instance's* claim about the row while
       * "Watched"/"Resume" is ours. The progress bar is untouched, so a flagged row the reader has
       * already half-watched still shows how far in it is.
       */
      item: row.notified ? { ...rendered, marker: text().subscriptions.unseen } : rendered,
      notified: row.notified,
      seen: isWatchedVideo(input.progress(row.video.videoId)),
    };
  });

  // The seen filter is display-only, like the manage page's name filter: the fold and the paging
  // proof stay about the *feed*. The hidden rows are no longer counted out loud — the notice is
  // retired — the Watched · Hidden menu wears the accent while it filters, which is the same fact
  // where the reader's eye already is.
  const unseen = input.hideSeen ? all.filter((entry) => !entry.seen) : all;

  // The grid search narrows after the seen filter — both display-only, so the order only decides
  // which sentence an emptied grid shows, and a typed search is the more recent intent.
  const items = unseen.filter((entry) =>
    matchesGridSearch(input.search, entry.item.title, entry.item.author),
  );

  return {
    kind: 'ready',
    items,
    nothingMatches: unseen.length > 0 && items.length === 0,
    notices,
    nextPage: nextSubscriptionFeedPage(input.pages, input.pageSize),
    listing,
  };
}

/** Unreachable — `toListingItems` is total over one video — but the type says it can be, so it is. */
function EMPTY_ITEM(videoId: string, index: number): ListingItem {
  return {
    videoId: videoId === '' ? `row-${String(index)}` : videoId,
    href: formatRoute({ kind: 'watch', videoId, startSec: null, playlistId: null, index: null }),
    title: '',
    author: '',
    authorHref: null,
    authorVerified: false,
    thumbnailUrl: null,
    durationSec: null,
    badge: null,
    viewCountText: null,
    viewCountTitle: null,
    publishedText: null,
    publishedTitle: null,
    marker: null,
    progress: null,
    watched: false,
  };
}

/* ------------------------------------------------------------------------------------------------
 * Copy
 * ---------------------------------------------------------------------------------------------- */

// The channel list's own state builder lived here until the strip moved to the manage page —
// `screens/channels/model.ts` holds it now, selection included.

function feedProblemSubject(): ProblemSubject {
  const words = text().subscriptions.problem;
  return {
    notFound: words.feedNotFound,
    unreadable: () => words.feedUnreadable,
    disabled: words.feedDisabled,
  };
}
